// Shared FAQ data — homepage + dedicated FAQ page
import { Link } from "react-router-dom";

const PORTAL = "my.melbiwellness.com";
const PORTAL_URL = "https://my.melbiwellness.com";
const portalLink = (
  <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-[#C96A4E] font-semibold hover:underline">
    {PORTAL}
  </a>
);

// Renewal / pause / cancel FAQs — shared by /faq and every treatment page.
export const RENEWAL_FAQS = [
  {
    q: "Does my plan renew automatically?",
    a: (
      <>
        Yes. Monthly plans renew every month and 3-month plans renew every 3 months, so your
        treatment continues without interruption. You can delay a shipment, pause, or cancel
        anytime from your patient portal at {portalLink}.
      </>
    ),
    aText:
      "Yes. Monthly plans renew every month and 3-month plans renew every 3 months, so your treatment continues without interruption. You can delay a shipment, pause, or cancel anytime from your patient portal at my.melbiwellness.com.",
  },
  {
    q: "How can I pause or cancel my treatment?",
    a: (
      <>
        <p>You can manage everything from your patient portal at {portalLink}.</p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            <span className="font-semibold text-[#2C2B29]">To delay your next shipment:</span> select
            “Manage Next Shipment” at the top of the page and choose a new date.
          </li>
          <li>
            <span className="font-semibold text-[#2C2B29]">To pause your treatment:</span> select
            Treatment, choose your treatment, then select “Pause Your Treatment.”
          </li>
          <li>
            <span className="font-semibold text-[#2C2B29]">To cancel your subscription:</span> select
            Treatment, then Manage, then “Cancel Membership.”
          </li>
        </ul>
        <p className="mt-3">
          Make any changes before your next billing date. For full details, see our{" "}
          <Link to="/legal/refund" className="text-[#C96A4E] font-semibold hover:underline">
            Cancellation, Refund, and Subscription Policy
          </Link>
          .
        </p>
      </>
    ),
    aText:
      "You can manage everything from your patient portal at my.melbiwellness.com. To delay your next shipment: select \"Manage Next Shipment\" at the top of the page and choose a new date. To pause your treatment: select Treatment, choose your treatment, then select \"Pause Your Treatment.\" To cancel your subscription: select Treatment, then Manage, then \"Cancel Membership.\" Make any changes before your next billing date. For full details, see our Cancellation, Refund, and Subscription Policy.",
  },
];

export const HOMEPAGE_FAQS = [
  {
    q: "Where is Melbi Wellness available?",
    a: (
      <>
        Melbi Wellness is available to eligible patients in all 50 U.S. states
        and the District of Columbia through licensed clinicians and pharmacy
        partners, subject to state-specific licensure and pharmacy availability.
        Certain medications, formulations, or fulfillment options may vary by
        state. For the full list of states and details on state-specific
        variation, see our{" "}
        <Link
          to="/service-areas"
          data-testid="faq-service-areas-link"
          className="text-[#C96A4E] font-semibold underline decoration-[#E8B5A1] underline-offset-2 hover:decoration-[#C96A4E] transition-colors"
        >
          Service Areas
        </Link>{" "}
        page.
      </>
    ),
  },
  {
    q: "What wellness categories does Melbi offer?",
    a: (
      <>
        <p>
          Melbi Wellness offers a focused selection of clinician-guided wellness categories designed to support a range of individual goals:
        </p>
        <ul className="mt-3 space-y-1.5 list-disc pl-5">
          <li><span className="font-semibold text-[#2C2B29]">Energy &amp; Focus</span> — B12 Injection</li>
          <li><span className="font-semibold text-[#2C2B29]">Antioxidant Support</span> — Glutathione Injection and Glutathione Nasal Spray</li>
          <li><span className="font-semibold text-[#2C2B29]">Vitality</span> — NAD+ Injection and NAD+ Nasal Spray</li>
          <li><span className="font-semibold text-[#2C2B29]">Metabolic Support</span> — MIC + B12 Injection</li>
          <li><span className="font-semibold text-[#2C2B29]">Recovery</span> — Sermorelin Injection</li>
          <li><span className="font-semibold text-[#2C2B29]">Weight Management</span> — Compounded Semaglutide and Compounded Tirzepatide</li>
          <li><span className="font-semibold text-[#2C2B29]">Skin Care</span> — GHK-Cu Face Cream</li>
        </ul>
        <p className="mt-3">
          All prescription treatments require review by a licensed healthcare provider and are prescribed only when clinically appropriate. Not all patients qualify, and product availability may vary by state.
        </p>
      </>
    ),
  },
  {
    q: "How do I get started?",
    a: "Click \"Start now\" to begin your secure online health intake. A licensed clinician will review your information and determine clinical appropriateness.",
  },
  {
    q: "Is clinical review required?",
    a: "Yes. A licensed clinician reviews every health intake and determines clinical appropriateness. Not all patients qualify.",
  },
  {
    q: "Who reviews my intake?",
    a: "A licensed clinician reviews your secure online health intake. They may approve, deny, request more information, or require a brief live phone or video visit.",
  },
  {
    q: "Where are prescriptions fulfilled?",
    a: (
      <>
        If prescribed, prescriptions are fulfilled by a licensed pharmacy partner and shipped
        directly to eligible patients.{" "}
        <Link to="/trust-your-source" className="text-[#C96A4E] font-semibold hover:underline">
          Learn why your medication&apos;s source matters.
        </Link>
      </>
    ),
  },
  {
    q: "Is approval guaranteed?",
    a: "No. Approval is never guaranteed. Not all patients qualify, and clinicians may deny treatment if it is not clinically appropriate.",
  },
  {
    q: "Are compounded medications FDA approved?",
    a: "No. Compounded medications, if prescribed, are not FDA approved and are not reviewed by the FDA for safety, effectiveness, or quality.",
  },
  {
    q: "How does pricing work?",
    a: "Melbi is no-insurance, direct-pay only. Insurance is not accepted. Pricing details are presented during the intake flow.",
  },
  {
    q: "Do you accept FSA or HSA?",
    a: "FSA / HSA may apply to some Melbi treatments depending on your plan and the medication prescribed. Check with your plan administrator.",
  },
  {
    q: "How do you handle pregnancy or breastfeeding?",
    a: "Pregnancy or breastfeeding may affect eligibility. A licensed clinician will determine clinical appropriateness based on your individual health history.",
  },
  {
    q: "How do monthly and 3-month plans work?",
    a: "Monthly plans are billed monthly. Three-month plans are billed in full every three months, with the price also displayed as a monthly equivalent for comparison. Available plans vary by treatment. Select ‘Compare Plans & Pricing’ on the treatment page to see the options and total charges before starting.",
  },
  ...RENEWAL_FAQS,
  {
    q: "Does my price go up as my dose increases?",
    a: "Your selected plan’s price does not increase solely because your clinician increases your GLP-1 dose within the doses available through that plan.",
  },
  {
    q: "Does Melbi take insurance?",
    a: "No. Melbi is cash pay and doesn't bill insurance. You may be able to pay with an HSA or FSA card when eligible, and we provide itemized receipts.",
  },
  {
    q: "Is there a membership fee?",
    a: "No. You pay only for your treatment plan. There's no separate membership fee.",
  },
  {
    q: "Do I need to talk to a doctor live?",
    a: "In most states, no. You complete a secure online intake, and a licensed clinician reviews it. Some states require a live video or phone visit before a prescription can be issued. If yours does, you'll be guided through it as part of your intake.",
  },
  {
    q: "How long does it take to get my medication?",
    a: "A licensed clinician typically reviews your intake within 24 hours. If treatment is appropriate, your prescription is sent to a licensed U.S. pharmacy. Once your medication ships, it typically arrives within 2 to 5 days. Shipping is free.",
  },
  {
    q: "Is Melbi Wellness legitimate?",
    a: (
      <>
        Yes. Melbi Wellness is{" "}
        <a
          href="https://www.legitscript.com/websites/?checker_keywords=melbiwellness.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#C96A4E] font-semibold hover:underline"
        >
          LegitScript certified
        </a>
        . Every request is reviewed by a licensed clinician, and prescriptions are filled by a
        state-licensed U.S. pharmacy.
      </>
    ),
  },
];
